import { useEffect, useMemo, useRef } from 'react';

export type BackGuard = {
  stay: () => void;
  leave: () => void;
  drop: () => void;
};

type Entry = { onBack: () => void };

const stack: Entry[] = [];
const present = new WeakSet<Entry>();
let ignoreUntil = 0;

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (Date.now() < ignoreUntil) {
      ignoreUntil = 0;
      return;
    }
    const top = stack[stack.length - 1];
    if (!top || !present.has(top)) return;
    present.delete(top);
    top.onBack();
  });
}

function removeEntry(entry: Entry) {
  const index = stack.indexOf(entry);
  if (index >= 0) stack.splice(index, 1);
}

export function useBackGuard(active: boolean, onBack: (guard: BackGuard) => void): BackGuard {
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  const handle = useMemo(() => {
    const guard: BackGuard = {
      stay: () => {
        present.add(entry);
        window.history.pushState({ backGuard: true }, '');
      },
      leave: () => {
        window.history.back();
      },
      drop: () => {
        present.delete(entry);
      },
    };
    const entry: Entry = { onBack: () => onBackRef.current(guard) };
    return { entry, guard };
  }, []);

  useEffect(() => {
    const { entry, guard } = handle;
    if (active) {
      if (!stack.includes(entry)) stack.push(entry);
      if (!present.has(entry)) guard.stay();
      return;
    }
    removeEntry(entry);
    if (present.has(entry)) {
      present.delete(entry);
      if (window.history.state?.backGuard === true) {
        ignoreUntil = Date.now() + 500;
        window.history.back();
      }
    }
  }, [active, handle]);

  useEffect(() => {
    const { entry } = handle;
    return () => removeEntry(entry);
  }, [handle]);

  return handle.guard;
}
